import { Link } from "react-router-dom";
import { ArrowRight, Network, Cable, Wrench, CheckCircle2 } from "lucide-react";

const FIBER_IMG = "https://images.unsplash.com/photo-1543273984-bb8287d2fc4e?auto=format&fit=crop&w=1200&q=80";
const COAX_IMG = "https://images.unsplash.com/photo-1595856619767-ab739fa7daae?auto=format&fit=crop&w=1200&q=80";
const SPLICING_IMG = "https://images.pexels.com/photos/11392632/pexels-photo-11392632.jpeg?auto=compress&w=1200";

const fiberCapabilities = [
  "Fiber backbone deployment",
  "Distribution network builds",
  "Aerial fiber construction",
  "Underground fiber installation",
  "Network expansion projects",
  "Infrastructure upgrades"
];

const coaxCapabilities = [
  "Aerial strand and coax installation",
  "Coax network builds and rebuilds",
  "HFC infrastructure deployment",
  "System upgrades and expansions"
];

const splicingCapabilities = [
  "Fusion splicing (single & ribbon)",
  "Fiber termination",
  "OTDR testing",
  "Power meter testing",
  "Troubleshooting & fault location",
  "Emergency restoration support"
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Our Services
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Comprehensive Telecommunications Services
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              From fiber optic construction to network testing, we provide end-to-end 
              telecommunications infrastructure solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Fiber Optic Network Construction */}
      <section id="fiber" className="py-20 md:py-32 bg-white scroll-mt-24" data-testid="fiber-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                  <Network className="h-6 w-6 text-white" />
                </div>
                <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                  Service 01
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Fiber Optic Network Construction (OSP)
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                C&F Communications provides Outside Plant (OSP) fiber construction services 
                for backbone and distribution networks. Our experienced crews deliver 
                high-quality fiber infrastructure that meets carrier standards.
              </p>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">Capabilities</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {fiberCapabilities.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-[#00B4D8] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)]"
                data-testid="fiber-cta"
              >
                Request Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src={FIBER_IMG}
                alt="Fiber Optic Network Construction"
                className="w-full h-auto object-cover"
              />
              <div className="absolute top-0 right-0 bg-[#0F172A] p-4">
                <Network className="h-8 w-8 text-[#00B4D8]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coax / HFC Network Construction */}
      <section id="coax" className="py-20 md:py-32 bg-slate-50 grid-pattern scroll-mt-24" data-testid="coax-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 relative">
              <img
                src={COAX_IMG}
                alt="Coax HFC Network Construction"
                className="w-full h-auto object-cover"
              />
              <div className="absolute top-0 left-0 bg-[#0F172A] p-4">
                <Cable className="h-8 w-8 text-[#00B4D8]" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                  <Cable className="h-6 w-6 text-white" />
                </div>
                <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                  Service 02
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Coax / HFC Network Construction
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We support broadband operators with coaxial and HFC network construction 
                services. Our teams have extensive experience in building and rebuilding 
                broadband infrastructure for major providers.
              </p>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">Capabilities</h3>
              <ul className="grid grid-cols-1 gap-3 mb-8">
                {coaxCapabilities.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-[#00B4D8] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)]"
                data-testid="coax-cta"
              >
                Request Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fiber Splicing & Testing */}
      <section id="splicing" className="py-20 md:py-32 bg-white scroll-mt-24" data-testid="splicing-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                  <Wrench className="h-6 w-6 text-white" />
                </div>
                <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                  Service 03
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Fiber Splicing & Testing
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Precision fusion splicing and network validation services for new builds 
                and restoration projects. Our technicians deliver high-quality splicing 
                with comprehensive testing and documentation.
              </p>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">Capabilities</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {splicingCapabilities.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-[#00B4D8] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)]"
                data-testid="splicing-cta"
              >
                Request Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src={SPLICING_IMG}
                alt="Fiber Splicing and Testing"
                className="w-full h-auto object-cover"
              />
              <div className="absolute top-0 right-0 bg-[#0F172A] p-4">
                <Wrench className="h-8 w-8 text-[#00B4D8]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#00B4D8]" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Need Custom Solutions?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Our team can help with unique project requirements. Let's discuss your needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-white text-[#0F172A] px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,0.3)]"
            data-testid="services-bottom-cta"
          >
            Contact Our Team
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
